import { createContext, PropsWithChildren, useEffect, useReducer, useRef } from 'react';
import { usePreservedCallback } from '@toss/react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import './events.web-payple';
import { trackEvent } from '@zep/logger/tracking';
import { Currency } from '@zep/types';
import { usePaypleAuthTokenMutation } from '../../../../../services';
import { isDevelopment } from '../../../../../utils';
import { getChargeZemPaypleServiceId, getDefaultPaypleServiceId } from './PaypleServiceId';
export const PapleContext = createContext<PaypleProviderProps>(undefined);
const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'SUCCESS':
      return {
        ...state,
        isSuccess: true,
        isFailure: false
      };
    case 'FAILURE':
      return {
        ...state,
        isSuccess: false,
        isFailure: true,
        failureMessage: action.payload
      };
    default:
      return state;
  }
};
export const PaypleProvider = (props: PaypleProviderInitProps) => {
  const {
    currency = 'USD'
  } = props;
  const router = useRouter();
  const {
    t
  } = useTranslation(['subscription']);
  const [paypleState, dispatch] = useReducer(reducer, {
    isSuccess: false,
    isFailure: false,
    failureMessage: ''
  });
  const paypleAuthTokenMutation = usePaypleAuthTokenMutation();
  // 결과 콜백이 오면 페이지가 이동하므로, 같은 화면에서 다시 열었다면 직전 결제창이 결과 없이 끝난 것.
  const lastOpenRef = useRef<{
    orderId: string;
    at: number;
    count: number;
  }>();
  const onFailRegisterBilling = usePreservedCallback(() => {
    const {
      query
    } = router;
    let message = '';
    if (query.status) {
      // ZEP ERROR
      message = query.status.toString();
    }
    // pg ERROR
    if (query.message) {
      message = query.message.toString();
    }
    return dispatch({
      type: 'FAILURE',
      payload: message || 'Processing Failure'
    });
  });
  const showWidget = async (payload: ShowWidgetPayload) => {
    if (payload.type === 'subscription') {
      payload.comments = t('subscription:payment.payple.prePaymentTitle');
    }
    const makeServiceDefine = () => {
      const allowedKeys = (key: string) => payload.type === 'business' ? ['email', 'businessPaymentHashId'].includes(key) : payload.type === 'singularSubscription' ? ['email', 'paymentRequestHashId', 'zipCode'].includes(key) : payload.type === 'onetime' ? ['playerEmail', 'spacePlanHashId', 'zipCode'].includes(key) : ['playerEmail', 'successRedirectUrl', 'failRedirectUrl', 'spaceHashId'].includes(key);
      const callbackUrlPayload = new URLSearchParams();
      for (const key in payload) {
        if (allowedKeys(key)) {
          callbackUrlPayload.append(key,
          // @ts-ignore
          payload[key]);
        }
      }
      return callbackUrlPayload;
    };
    const service_id = (() => {
      if (payload.type === 'chargeZem') {
        return getChargeZemPaypleServiceId({
          currency,
          isTest: isDevelopment
        });
      }
      return getDefaultPaypleServiceId({
        isTest: isDevelopment
      });
    })();
    const widgetProps = {
      payment_type: payload.type,
      order_id: payload.orderId ?? '',
      currency,
      total_amount: payload.totalAmount ?? 1
    };
    let stage: 'auth_token' | 'open' = 'auth_token';
    try {
      const res = await paypleAuthTokenMutation.mutateAsync({
        currency,
        type: payload.type
      });
      stage = 'open';
      const now = Date.now();
      const last = lastOpenRef.current;
      trackEvent('web_payple_widget_open', {
        ...widgetProps,
        retry_count: last?.count ?? 0,
        since_last_open_ms: last ? now - last.at : undefined,
        last_order_id: last?.orderId
      });
      lastOpenRef.current = {
        orderId: widgetProps.order_id,
        at: now,
        count: (last?.count ?? 0) + 1
      };
      paypleGpayPaymentRequest(
      // @ts-ignore
      {
        Authorization: res.data.accessToken,
        currency,
        totalAmount: payload.totalAmount ?? 1,
        comments: payload.comments,
        resultUrl: payload.callbackUrl,
        serviceDefine: makeServiceDefine().toString(),
        service_id,
        service_oid: payload.orderId ?? '',
        isRedirect: '',
        ...((payload.type === 'singularSubscription' || payload.type === 'onetime') && {
          zipCode: payload.zipCode
        }),
        ...(isDevelopment && {
          payCls: 'demo'
        })
      });
    } catch (e) {
      console.log(e);
      trackEvent('web_payple_widget_error', {
        ...widgetProps,
        stage,
        message: e instanceof Error ? e.message : String(e)
      });
      dispatch({
        type: 'FAILURE',
        payload: 'Error Payple Widget'
      });
      console.error(e);
    }
  };
  useEffect(() => {
    const loadPaypleScript = () => {
      const script = document.createElement('script');
      script.src = 'https://gpay.payple.kr/common/js/gpay-1.0.1.js';
      script.async = false;
      script.onerror = () => trackEvent('web_payple_script_error', {
        src: script.src
      });
      document.head.appendChild(script);
    };
    // jquery 랑 강력히 연계되어있습니다.
    const jqueryScript = document.createElement('script');
    jqueryScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/jquery/3.6.0/jquery.min.js';
    jqueryScript.onload = loadPaypleScript;
    jqueryScript.onerror = () => trackEvent('web_payple_script_error', {
      src: jqueryScript.src
    });
    document.head.appendChild(jqueryScript);
  }, []);
  useEffect(() => {
    const clearRouterQuery = () => {
      const {
        query,
        pathname
      } = router;
      if (query.status) delete query.status;
      if (query.result) delete query.result;
      if (query.message) delete query.message;
      delete query.isBillingRegister;

      // 서버에서 받은 pg 응답을 제거합니다.
      void router.replace({
        pathname,
        query
      }, undefined, {
        shallow: true
      });
    };
    if (router.query.isBillingRegister) {
      router.query.isBillingRegister === 'true' ? dispatch({
        type: 'SUCCESS'
      }) : onFailRegisterBilling();
      clearRouterQuery();
    }
  }, [router, onFailRegisterBilling]);
  return <PapleContext.Provider value={{
    ...props,
    isSuccess: paypleState.isSuccess,
    isFailure: paypleState.isFailure,
    failMessage: Number(paypleState.failureMessage) < 0 ? `ERROR CODE(${paypleState.failureMessage})` : paypleState.failureMessage,
    showWidget
  }} data-sentry-element="unknown" data-sentry-component="PaypleProvider" data-sentry-source-file="PaypleProvider.tsx">
      {props.children}
    </PapleContext.Provider>;
};
type PaypleProviderProps = {
  isSuccess: boolean;
  isFailure: boolean;
  failMessage: string;
  /** 위의 3개는 정기결제에서만 사용하는 값들입니다. 성공 실패 유무를 페이지 이동을 하지않고 알아야 될 경우 사용하면됩니다 */
  showWidget: (payload: ShowWidgetPayload) => Promise<void>;
} | undefined;
type PaypleProviderInitProps = PropsWithChildren<{
  currency?: Currency;
}>;
type State = {
  isSuccess: boolean;
  isFailure: boolean;
  failureMessage: string;
};
declare function paypleGpayPaymentRequest(payload: {
  Authorization: string;
  service_id: string;
  service_oid: string;
  comments: string;
  currency: string;
  totalAmount: number;
  resultUrl: string;
  serviceDefine: string;
  isRedirect: 'Y' | '';
  payCls?: 'demo';
  zipCode: string;
}): void;
type Action = {
  type: 'SUCCESS';
} | {
  type: 'FAILURE';
  payload: string;
};
type ShowWidgetPayload = SubscriptionWidgetPayload | BusinessPaymentWidgetPayload | SingularSubscriptionWidgetPayload | ChargeZemWidgetPayload | OnetimeWidgetPayload;
type ShowWidgetDefaultPayload = {
  orderId: string;
  comments: string;
  callbackUrl: string;
  totalAmount: number;
};
type BusinessPaymentWidgetPayload = ShowWidgetDefaultPayload & {
  type: 'business';
  email: string;
  businessPaymentHashId: string;
};
type SingularSubscriptionWidgetPayload = ShowWidgetDefaultPayload & {
  type: 'singularSubscription';
  email: string;
  paymentRequestHashId: string;
  zipCode?: string;
};
type SubscriptionWidgetPayload = ShowWidgetDefaultPayload & {
  type: 'subscription';
  spaceHashId: string;
  failRedirectUrl: string;
  successRedirectUrl: string;
  playerEmail: string;
};
type OnetimeWidgetPayload = ShowWidgetDefaultPayload & {
  type: 'onetime';
  spacePlanHashId: string;
  playerEmail: string;
  zipCode?: string;
};
type ChargeZemWidgetPayload = ShowWidgetDefaultPayload & {
  type: 'chargeZem';
};
export const PaypleWidgetDefaultPayload = {
  orderId: '',
  totalAmount: 1,
  comments: ''
};